/**
 * Modificadores de corrida da Batalha Infinita.
 *
 * As cartas escolhidas entre ondas não editam o tanque nem o estado do jogo:
 * elas se acumulam neste objeto, e os poucos pontos do jogo que importam
 * (disparo, movimento, spawn da onda, abate) leem daqui. Manter um único
 * agregado é o que impede o draft de virar um emaranhado de casos especiais
 * espalhados por Tank.tsx, playerWeapon.ts e o store.
 *
 * Todo campo tem identidade neutra definida em `NEUTRAL_MODIFIERS`: uma corrida
 * sem nenhuma carta produz exatamente o comportamento anterior ao draft.
 */

export interface RunModifiers {
  /** Multiplicador do dano por disparo. */
  damageMul: number;
  /** Vida máxima adicional (plano, somado ao tanque escolhido). */
  maxHealthBonus: number;
  /** Multiplicador da velocidade de deslocamento. */
  speedMul: number;
  /** Projéteis extras por disparo. */
  shotCountBonus: number;
  /** Quantos inimigos cada projétil atravessa antes de sumir. */
  pierce: number;
  /** Multiplicador da velocidade do projétil. */
  bulletSpeedMul: number;
  /** Cura recebida a cada abate. */
  lifestealPerKill: number;
  /** Raio em que itens são atraídos para o tanque (0 = desligado). */
  magnetRadius: number;
  /** Fração do dano de terreno (lava) que é ignorada. 0..1 */
  hazardResist: number;
  /** Tração mínima garantida no gelo (1 = imune a derrapagem). */
  iceGrip: number;
  /** Fração do dano recebido que é absorvida. 0..1 */
  armor: number;
  /** Multiplicador da lentidão de água/lama (1 = sofre tudo, 0 = imune). */
  wadingPenalty: number;
  /** Rerolls extras no draft. */
  extraRerolls: number;
  /** Cura extra concedida ao fim de cada onda. */
  waveHealBonus: number;
}

export const NEUTRAL_MODIFIERS: Readonly<RunModifiers> = Object.freeze({
  damageMul: 1,
  maxHealthBonus: 0,
  speedMul: 1,
  shotCountBonus: 0,
  pierce: 0,
  bulletSpeedMul: 1,
  lifestealPerKill: 0,
  magnetRadius: 0,
  hazardResist: 0,
  iceGrip: 0,
  armor: 0,
  wadingPenalty: 1,
  extraRerolls: 0,
  waveHealBonus: 0,
});

/**
 * Contribuição de uma carta. Campos multiplicativos são compostos por produto,
 * aditivos por soma, e `iceGrip`/`hazardResist`/`armor` pelo maior valor — três
 * cartas de blindagem não podem zerar o dano recebido.
 */
export interface ModifierDelta {
  damageMul?: number;
  maxHealthBonus?: number;
  speedMul?: number;
  shotCountBonus?: number;
  pierce?: number;
  bulletSpeedMul?: number;
  lifestealPerKill?: number;
  magnetRadius?: number;
  hazardResist?: number;
  iceGrip?: number;
  armor?: number;
  wadingPenalty?: number;
  extraRerolls?: number;
  waveHealBonus?: number;
}

/** Tetos de segurança: impedem que um empilhamento improvável quebre o jogo. */
const CAPS = {
  damageMul: 6,
  speedMul: 2.2,
  shotCountBonus: 6,
  pierce: 4,
  bulletSpeedMul: 2.5,
  armor: 0.6,
  hazardResist: 0.9,
  magnetRadius: 18,
  maxHealthBonus: 400,
  lifestealPerKill: 25,
  waveHealBonus: 60,
} as const;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Dobra a lista de deltas em um único agregado. Ordem não importa — a operação
 * é comutativa em todos os campos, para que a mesma coleção de cartas produza
 * sempre o mesmo tanque independentemente da ordem em que foram escolhidas.
 */
export function aggregateModifiers(deltas: readonly ModifierDelta[]): RunModifiers {
  const acc: RunModifiers = { ...NEUTRAL_MODIFIERS };

  for (const delta of deltas) {
    if (delta.damageMul !== undefined) acc.damageMul *= delta.damageMul;
    if (delta.speedMul !== undefined) acc.speedMul *= delta.speedMul;
    if (delta.bulletSpeedMul !== undefined) acc.bulletSpeedMul *= delta.bulletSpeedMul;
    if (delta.wadingPenalty !== undefined) acc.wadingPenalty *= delta.wadingPenalty;

    if (delta.maxHealthBonus !== undefined) acc.maxHealthBonus += delta.maxHealthBonus;
    if (delta.shotCountBonus !== undefined) acc.shotCountBonus += delta.shotCountBonus;
    if (delta.pierce !== undefined) acc.pierce += delta.pierce;
    if (delta.lifestealPerKill !== undefined) acc.lifestealPerKill += delta.lifestealPerKill;
    if (delta.extraRerolls !== undefined) acc.extraRerolls += delta.extraRerolls;
    if (delta.waveHealBonus !== undefined) acc.waveHealBonus += delta.waveHealBonus;

    // Saturantes: o melhor valor vence, empilhar não soma.
    if (delta.magnetRadius !== undefined) acc.magnetRadius = Math.max(acc.magnetRadius, delta.magnetRadius);
    if (delta.hazardResist !== undefined) acc.hazardResist = Math.max(acc.hazardResist, delta.hazardResist);
    if (delta.iceGrip !== undefined) acc.iceGrip = Math.max(acc.iceGrip, delta.iceGrip);
    if (delta.armor !== undefined) acc.armor = Math.max(acc.armor, delta.armor);
  }

  acc.damageMul = clamp(acc.damageMul, 0.1, CAPS.damageMul);
  acc.speedMul = clamp(acc.speedMul, 0.4, CAPS.speedMul);
  acc.bulletSpeedMul = clamp(acc.bulletSpeedMul, 0.5, CAPS.bulletSpeedMul);
  acc.wadingPenalty = clamp(acc.wadingPenalty, 0, 1);
  acc.shotCountBonus = clamp(Math.round(acc.shotCountBonus), 0, CAPS.shotCountBonus);
  acc.pierce = clamp(Math.round(acc.pierce), 0, CAPS.pierce);
  acc.maxHealthBonus = clamp(Math.round(acc.maxHealthBonus), -80, CAPS.maxHealthBonus);
  acc.lifestealPerKill = clamp(acc.lifestealPerKill, 0, CAPS.lifestealPerKill);
  acc.waveHealBonus = clamp(acc.waveHealBonus, 0, CAPS.waveHealBonus);
  acc.magnetRadius = clamp(acc.magnetRadius, 0, CAPS.magnetRadius);
  acc.hazardResist = clamp(acc.hazardResist, 0, CAPS.hazardResist);
  acc.iceGrip = clamp(acc.iceGrip, 0, 1);
  acc.armor = clamp(acc.armor, 0, CAPS.armor);

  return acc;
}
